import { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type Column,
  type ColumnDef,
  type ColumnFiltersState,
  type RowData,
  type SortingState,
} from "@tanstack/react-table";
import { cn } from "../../lib/utils";
import { ArrowUpDown, ArrowUp, ArrowDown, Search, X } from "lucide-react";
import { Input } from "./Input";
import { LoadingSpinner } from "./LoadingSpinner";

/** Per-column filter shape consumed by DataTable. Set this on a column
 *  via `meta: { filter: { ... } }` and the DataTable will render a
 *  matching control in a second header row. Pair with `filterFn` on the
 *  same column to customize matching (default for `select` is exact
 *  equality, default for `text` is the column's accessor + includesString). */
export type ColumnFilterConfig =
  | { type: "text"; placeholder?: string }
  | {
      type: "select";
      options: { value: string; label: string }[];
      placeholder?: string;
    };

// Module augmentation so `column.columnDef.meta?.filter` is typed.
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    filter?: ColumnFilterConfig;
  }
}

interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  isLoading?: boolean;
  searchPlaceholder?: string;
  enableSearch?: boolean;
  emptyMessage?: string;
  onRowClick?: (row: TData) => void;
  page?: number;
  totalPages?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  /** Controlled search value. When provided, the table filters by this
   *  string and ignores its internal search state. Pair with
   *  `onSearchChange` and `enableSearch={false}` to render the input in a
   *  parent component (e.g. inline with page header buttons). */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

export function DataTable<TData>({
  columns,
  data,
  isLoading = false,
  searchPlaceholder = "Search...",
  enableSearch = true,
  emptyMessage = "No data found",
  onRowClick,
  page,
  totalPages,
  total,
  onPageChange,
  searchValue,
  onSearchChange,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [internalFilter, setInternalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const isControlled = searchValue !== undefined;
  const globalFilter = isControlled ? searchValue : internalFilter;
  const setGlobalFilter = isControlled
    ? (v: string) => onSearchChange?.(v)
    : setInternalFilter;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(globalFilter) : updater;
      setGlobalFilter(next as string);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // Are any columns declaring a filter input? Only then do we render the
  // second header row — other DataTable callers see no visual change.
  const hasColumnFilters = columns.some((c) => c.meta?.filter !== undefined);

  return (
    <div className="space-y-4">
      {enableSearch && (
        <div className="max-w-sm">
          <Input
            placeholder={searchPlaceholder}
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            icon={<Search className="h-4 w-4" />}
          />
        </div>
      )}

      <div className="rounded-xl border border-surface-700/50 bg-surface-800/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className={cn(
                    "border-b border-surface-700/50",
                    hasColumnFilters ? "border-b-surface-700/30" : "",
                  )}
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={cn(
                        "px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-surface-400",
                        header.column.getCanSort() &&
                          "cursor-pointer select-none hover:text-surface-200"
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-1.5">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {header.column.getCanSort() && (
                          <span className="text-surface-500">
                            {header.column.getIsSorted() === "asc" ? (
                              <ArrowUp className="h-3.5 w-3.5" />
                            ) : header.column.getIsSorted() === "desc" ? (
                              <ArrowDown className="h-3.5 w-3.5" />
                            ) : (
                              <ArrowUpDown className="h-3.5 w-3.5" />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
              {/* Per-column filter row — only rendered when at least one
                  column declares meta.filter. Click handling is stopped
                  inside the input so the cell's sort toggler doesn't fire
                  while the user types or picks an option. */}
              {hasColumnFilters &&
                table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={`filters-${headerGroup.id}`}
                    className="border-b border-surface-700/50 bg-surface-900/40"
                  >
                    {headerGroup.headers.map((header) => (
                      <th key={`f-${header.id}`} className="px-3 pb-2 pt-1 align-top">
                        {header.column.columnDef.meta?.filter ? (
                          <ColumnFilterInput
                            column={header.column}
                            config={header.column.columnDef.meta.filter}
                          />
                        ) : null}
                      </th>
                    ))}
                  </tr>
                ))}
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="py-16 text-center"
                  >
                    <LoadingSpinner size="md" label="Loading data..." />
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="py-16 text-center text-surface-500"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={cn(
                      "border-b border-surface-700/30 transition-colors",
                      onRowClick &&
                        "cursor-pointer hover:bg-surface-700/30"
                    )}
                    onClick={() => onRowClick?.(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-3 py-2 text-sm text-surface-300 align-middle"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages !== undefined && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-surface-400">
            {total !== undefined ? `${total} total results` : ""}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange?.(Math.max(1, (page || 1) - 1))}
              disabled={page === 1}
              className="rounded-lg border border-surface-700 px-3 py-1.5 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <span className="text-surface-400">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() =>
                onPageChange?.(
                  Math.min(totalPages, (page || 1) + 1)
                )
              }
              disabled={page === totalPages}
              className="rounded-lg border border-surface-700 px-3 py-1.5 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Renders the per-column filter control based on the column's meta.filter
 *  config. Click events are stopped from bubbling so the parent `<th>`'s
 *  sort handler doesn't fire while the user interacts with the control. */
function ColumnFilterInput<TData, TValue>({
  column,
  config,
}: {
  column: Column<TData, TValue>;
  config: ColumnFilterConfig;
}) {
  const raw = column.getFilterValue();
  const value = typeof raw === "string" ? raw : "";

  if (config.type === "text") {
    return (
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative"
      >
        <input
          type="text"
          value={value}
          onChange={(e) => column.setFilterValue(e.target.value || undefined)}
          onClick={(e) => e.stopPropagation()}
          placeholder={config.placeholder ?? "Filter…"}
          className="w-full rounded-md border border-surface-700 bg-surface-900 px-2 py-1 pr-6 text-[11px] font-normal normal-case tracking-normal text-surface-100 placeholder:text-surface-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        {value && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              column.setFilterValue(undefined);
            }}
            className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-surface-500 hover:bg-white/5 hover:text-surface-200"
            title="Clear filter"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  // select
  return (
    <select
      value={value}
      onChange={(e) => column.setFilterValue(e.target.value || undefined)}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        "w-full rounded-md border border-surface-700 bg-surface-900 px-2 py-1 text-[11px] font-normal normal-case tracking-normal focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
        value ? "text-surface-100" : "text-surface-500",
      )}
    >
      <option value="">{config.placeholder ?? "All"}</option>
      {config.options.map((o) => (
        <option key={o.value} value={o.value} className="text-surface-100">
          {o.label}
        </option>
      ))}
    </select>
  );
}
